'use client';

import React from 'react';

interface Badge {
  icon: string;
  label: string;
}

interface StorySectionProps {
  title: string;           // e.g. "Roasted with Intention."
  subtitle: string;        // e.g. "Our Philosophy"
  description: string[];   // array of paragraphs
  badges: Badge[];
  imageUrl: string;
}

export default function StorySection({ title, subtitle, description, badges, imageUrl }: StorySectionProps) {
  return (
    <section id="story" className="py-32 overflow-hidden" style={{ background: "var(--roast)" }}>
      <div className="max-w-7xl mx-auto px-8 md:px-16 grid md:grid-cols-2 gap-24 items-center">
        {/* Left: decorative image */}
        <div data-reveal="left" className="relative">
          <div className="relative w-full aspect-square max-w-md mx-auto">
            {[1, 0.75, 0.5].map((scale, i) => (
              <div key={i} className="absolute inset-0 rounded-full border"
                style={{
                  borderColor: `rgba(200,135,42,${0.05 + i * 0.05})`,
                  transform: `scale(${scale})`,
                  margin: "auto",
                  top: 0, left: 0, right: 0, bottom: 0,
                }} />
            ))}
            <div className="absolute inset-[15%] rounded-full flex flex-col items-center justify-center gap-4 text-center"
              style={{
                background: "linear-gradient(135deg, var(--smoke), var(--espresso))",
                border: "1px solid rgba(200,135,42,.2)",
                boxShadow: "0 40px 80px rgba(0,0,0,.4)",
              }}>
              <img src={imageUrl} alt="Story visual" className="w-32 h-32 object-contain" />
            </div>
          </div>
        </div>

        {/* Right: text */}
        <div>
          <div data-reveal className="ornament-divider mb-6 max-w-xs">
            <span className="text-xs tracking-widest uppercase" style={{ color: "var(--amber)" }}>{subtitle}</span>
          </div>
          <h2 data-reveal className="text-5xl md:text-6xl font-black mb-8 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            {title}
          </h2>
          <div data-reveal className="space-y-5 leading-relaxed opacity-60 text-base mb-10">
            {description.map((para, idx) => (
              <p key={idx}>{para}</p>
            ))}
          </div>

          <div data-reveal data-stagger className="grid grid-cols-3 gap-4">
            {badges.map((badge, idx) => (
              <div key={idx} className="text-center p-4 rounded-2xl"
                style={{ background: "rgba(200,135,42,.08)", border: "1px solid rgba(200,135,42,.15)" }}>
                <div className="text-2xl mb-2">{badge.icon}</div>
                <div className="text-xs tracking-wider uppercase opacity-60">{badge.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}